import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

import MonthCalendar from "../components/shifts/MonthCalendar";
import EmployeeStatsPanel from "../components/shifts/EmployeeStatsPanel";
import WeekPlanner from "../components/shifts/WeekPlanner";
import useEmployees from "../hooks/useEmployees";
import {
  isCzechHoliday,
  isBigShopClosedHoliday,
} from "../utils/czechHolidays";

// Přesné vytvoření lokálního data z "YYYY-MM-DD"
function makeLocalDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Lokální Date -> "YYYY-MM-DD" (BEZ toISOString kvůli UTC posunu)
function formatISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// 🔥 Funkce: z vybraného dne (ISO) vrátí celý týden Po–Ne
function getWeekFromDate(isoDate) {
  const d = makeLocalDate(isoDate);
  const day = d.getDay(); // 0=Ne,1=Po...
  const diff = day === 0 ? -6 : 1 - day; // posun na pondělí

  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);

  const week = [];
  for (let i = 0; i < 7; i++) {
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + i);

    week.push({
      iso: formatISO(dt), // ❗ NEPOUŽÍVAT toISOString()
      jsDate: dt,
      weekPosition: i, // 0=Po ... 6=Ne
    });
  }
  return week;
}

// helper: normalizace názvů pro tooltipy
function humanLeaveType(t) {
  switch (t) {
    case "dovolena":
      return "Dovolená";
    case "lekar":
      return "Lékař";
    case "nahradni-volno":
      return "Náhradní volno";
    case "jine":
      return "Jiné";
    case "ranni":
      return "Požadavek: ranní";
    case "odpo":
      return "Požadavek: odpolední";
    case "celodenni":
      return "Požadavek: celodenní";
    default:
      return t;
  }
}

// request type -> shift code
function requestTypeToShiftCode(t) {
  if (t === "ranni") return "R";
  if (t === "odpo") return "O";
  if (t === "celodenni") return "C";
  return null;
}

function positionToRole(position) {
  if (!position) return "assistant";
  const p = String(position).toLowerCase();

  if (p.includes("farm")) return "magistr";   // farmaceut -> magistr
  if (p.includes("asist")) return "assistant";
  if (p.includes("sanit")) return "sanitar";

  // fallback
  return "assistant";
}

export default function ShiftsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const modeParam = params.get("mode") || "draft";
  const qpPlanId = params.get("planId");
  const qpPharmacy = params.get("pharmacy");
  const qpYear = params.get("year");
  const qpMonth = params.get("month");


  const isManager = user?.role === "admin" || user?.role === "rm";
  const isReviewMode = isManager && modeParam === "review";

  // ✅ když otevřu konkrétní plán přes planId, bereme pobočku z plánu (ne z admina=0000)
  const [openedPlanPharmacyCode, setOpenedPlanPharmacyCode] = useState(null);

  const targetPharmacyCode =
    qpPlanId
      ? (openedPlanPharmacyCode || null)
      : (isReviewMode && qpPharmacy ? qpPharmacy : user?.pharmacyCode);



  const reviewYear = isReviewMode && qpYear ? Number(qpYear) : null;
  const reviewMonth = isReviewMode && qpMonth ? Number(qpMonth) : null;

  // ✅ kmenoví zaměstnanci cílové pobočky (hook musí umět param)
  const { employees, loading: loadingEmployees } = useEmployees(targetPharmacyCode);

  const today = new Date();
  const fallbackYear = today.getFullYear();
  const fallbackMonth =
    today.getDate() >= 15 ? today.getMonth() + 2 : today.getMonth() + 1;

  const initialYear =
    reviewYear && Number.isFinite(reviewYear) ? reviewYear : fallbackYear;

  const initialMonthRaw =
    reviewMonth && Number.isFinite(reviewMonth) ? reviewMonth : fallbackMonth;

  const initialMonth = initialMonthRaw > 12 ? 1 : initialMonthRaw;

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);

  const [weekDays, setWeekDays] = useState(null);

  // létači – pouze pro review režim (admin/RM)
  const [flyingEmployees, setFlyingEmployees] = useState([]);

  // hover tooltip nad létačem
  const [hoverFlyerId, setHoverFlyerId] = useState(null);

  // ✅ obsazenost létačů napříč pobočkami (tooltip + kolize)
  const [flyingOccupancy, setFlyingOccupancy] = useState({});
  const [loadingFlyingOccupancy, setLoadingFlyingOccupancy] = useState(false);


  const [planStatus, setPlanStatus] = useState("draft");
  const [shifts, setShifts] = useState({});
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [planId, setPlanId] = useState(null);



  // ✅ omezení z dovolených/požadavků – týden (tooltipy, planner)
  const [leaveConstraintsByDate, setLeaveConstraintsByDate] = useState({});
  const [loadingConstraints, setLoadingConstraints] = useState(false);

  // ✅ absence pro STATS – celý měsíc (ne jen týden)
  const [monthlyAbsenceByDate, setMonthlyAbsenceByDate] = useState({});
  const [loadingMonthlyAbsence, setLoadingMonthlyAbsence] = useState(false);


  // ✅ openingHours cílové pobočky (pro review)
  const [targetOpeningHours, setTargetOpeningHours] = useState(null);
  const openingReady = !!targetOpeningHours;


  // readOnly:
  // - approved / superseded / canceled vždy read-only
  // - pending může upravovat jen admin/RM
  const readOnly =
    ["approved", "superseded", "canceled"].includes(planStatus) ||
    (!isManager && planStatus === "pending");



  // =========================
  // Načtení plánu směn
  // 1) pokud je v URL planId -> načti konkrétní verzi přes /by-id/:planId
  // 2) jinak -> načti poslední verzi pro měsíc/pobočku
  // =========================
  useEffect(() => {
    if (!user?.firma) return;

    const loadPlan = async () => {
      try {
        setLoadingPlan(true);
        setWeekDays(null);

        // ✅ otevření konkrétní verze
        if (qpPlanId) {
          const res = await fetch(`/api/shifts/by-id/${qpPlanId}`, {
            credentials: "include",
          });
          const data = await res.json().catch(() => null);

          if (!res.ok) {
            console.error("Chyba načítání plánu (by-id):", data);
            return;
          }

          setPlanId(data?._id || null);
          setOpenedPlanPharmacyCode(data?.pharmacyCode || null);

          // sjednotíme měsíc/rok podle otevřeného plánu
          if (Number.isFinite(data?.year)) setYear(data.year);
          if (Number.isFinite(data?.month)) setMonth(data.month);

          // ✅ u approved plánů bereme primárně snapshot (read-only)
          const resolvedShifts = data?.snapshot?.shifts || data?.shifts || {};
          setShifts(resolvedShifts);
          setPlanStatus(data?.status || "draft");
          return;

        }

        // ✅ běžné načtení poslední verze podle měsíce/pobočky
        if (!targetPharmacyCode) return;

        const res = await fetch(
          `/api/shifts/${user.firma}/${targetPharmacyCode}/${year}/${month}`,
          { credentials: "include" }
        );
        const data = await res.json().catch(() => null);

        if (!res.ok) {
          console.error("Chyba načítání plánu (latest):", data);
          return;
        }

        setPlanId(data?._id || null);
        setOpenedPlanPharmacyCode(null);

        setShifts(data?.shifts || {});
        setPlanStatus(data?.status || "draft");
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPlan(false);
      }
    };

    loadPlan();
  }, [user?.firma, qpPlanId, targetPharmacyCode, year, month, location.search]);




  // =========================
  // ✅ (review) PŘEDNAČTENÍ LÉTAČŮ pro celý měsíc
  // aby MonthCalendar hned ukazoval fajfky (coverageByDate)
  // =========================
  useEffect(() => {
    if (!user || !user.firma || !targetPharmacyCode) return;

    if (!isReviewMode || !isManager) {
      setFlyingEmployees([]);
      return;
    }

    const getMondaysForMonth = (y, m1to12) => {
      const first = new Date(y, m1to12 - 1, 1);
      const last = new Date(y, m1to12, 0);

      // pondělí týdne, do kterého spadá 1. den měsíce
      const jsDay = first.getDay(); // 0=Ne
      const diff = jsDay === 0 ? -6 : 1 - jsDay;
      const monday = new Date(first);
      monday.setDate(first.getDate() + diff);

      const mondays = [];
      const cur = new Date(monday);

      while (cur <= last) {
        mondays.push(formatISO(cur));
        cur.setDate(cur.getDate() + 7);
      }

      return mondays;
    };

    const loadFlyingForMonth = async () => {
      try {
        const mondays = getMondaysForMonth(year, month);

        const results = await Promise.all(
          mondays.map(async (weekIso) => {
            const res = await fetch(
              `/api/employees/${user.firma}/flying/week?pharmacyCode=${encodeURIComponent(
                targetPharmacyCode
              )}&week=${encodeURIComponent(weekIso)}`,
              { credentials: "include" }
            );
            const data = await res.json();
            return Array.isArray(data?.items) ? data.items : [];
          })
        );

        // merge podle _id (spojujeme scheduleByDate/freeOptionsByDate napříč týdny)
        const byId = {};
        for (const arr of results) {
          for (const e of arr) {
            if (!e?._id) continue;
            const id = String(e._id);

            if (!byId[id]) {
              byId[id] = {
                ...e,
                scheduleByDate: e.scheduleByDate || {},
                freeOptionsByDate: e.freeOptionsByDate || {},
              };
            } else {
              byId[id].scheduleByDate = { ...(byId[id].scheduleByDate || {}), ...(e.scheduleByDate || {}) };
              byId[id].freeOptionsByDate = { ...(byId[id].freeOptionsByDate || {}), ...(e.freeOptionsByDate || {}) };
            }
          }
        }

        const mapped = Object.values(byId).map((e) => ({
          id: String(e._id),
          name: e.fullName,
          role: positionToRole(e.position),
          fullFond: Number(e.denniFond || 0),
          isFlying: true,
          allowedPharmacyCodes: Array.isArray(e.allowedPharmacyCodes) ? e.allowedPharmacyCodes : [],
          scheduleByDate: e.scheduleByDate || {},
          freeOptionsByDate: e.freeOptionsByDate || {},
        }));

        setFlyingEmployees(mapped);
      } catch (err) {
        console.error("Chyba načítání létačů pro měsíc:", err);
        setFlyingEmployees([]);
      }
    };

    loadFlyingForMonth();
  }, [user, isReviewMode, isManager, targetPharmacyCode, year, month]);



  // =========================
  // Načtení otevírací doby cílové pobočky
  // =========================
  useEffect(() => {
    if (!user || !user.firma || !targetPharmacyCode) return;

    const loadOpeningHours = async () => {
      try {
        // moje pobočka => rovnou z user
        if (targetPharmacyCode === user.pharmacyCode) {
          setTargetOpeningHours(user.openingHours || null);
          return;
        }

        const res = await fetch(`/api/users/${user.firma}/${targetPharmacyCode}`, {
          credentials: "include",
        });
        const data = await res.json();
        setTargetOpeningHours(data?.openingHours || null);
      } catch (e) {
        console.error("Chyba načítání otevírací doby cílové pobočky:", e);
        setTargetOpeningHours(null);
      }
    };

    loadOpeningHours();
  }, [user, targetPharmacyCode]);

  const effectiveOpeningHours = targetOpeningHours || user?.openingHours || null;

  // =========================
  // Formátování týdnů Po–Ne
  // =========================
  const formattedWeekDays = useMemo(() => {
    if (!weekDays) return [];

    return weekDays
      .map((d) => ({
        date: d.iso,
        jsDate: d.jsDate,
        weekPosition: d.weekPosition,
        label: d.jsDate.toLocaleDateString("cs-CZ", {
          weekday: "long",
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }),
      }))
      .sort((a, b) => a.weekPosition - b.weekPosition);
  }, [weekDays]);

  // =========================
  // Načtení omezení (dovolené/požadavky) pro vybraný týden
  // =========================
  useEffect(() => {
    if (!user || !user.firma || !targetPharmacyCode) return;
    if (!formattedWeekDays.length) {
      setLeaveConstraintsByDate({});
      return;
    }

    const loadConstraints = async () => {
      try {
        setLoadingConstraints(true);

        const res = await fetch(`/api/leaves/${user.firma}/${targetPharmacyCode}`, {
          credentials: "include",
        });

        if (!res.ok) {
          const t = await res.text().catch(() => "");
          console.warn("LEAVES fetch failed:", res.status, t);
          setLeaveConstraintsByDate({});
          return;
        }

        const all = await res.json().catch(() => null);
        if (!Array.isArray(all)) {
          setLeaveConstraintsByDate({});
          return;
        }

        // týdenní rozsah
        const fromIso = formattedWeekDays[0].date;
        const toIso = formattedWeekDays[formattedWeekDays.length - 1].date;
        const from = makeLocalDate(fromIso);
        from.setHours(0, 0, 0, 0);
        const to = makeLocalDate(toIso);
        to.setHours(23, 59, 59, 999);

        const BLOCKING_TYPES = new Set(["dovolena", "lekar", "nahradni-volno", "jine"]);
        const map = {}; // { [isoDate]: { [employeeId]: { blocked, blockTitle, request, requestTitle } } }

        for (const r of all) {
          const status = r?.status;

          const empId =
            typeof r.employeeId === "string"
              ? r.employeeId
              : r.employeeId?._id || r.employeeId?.id || null;

          if (!empId) continue;

          const days = Array.isArray(r.days) ? r.days : [];
          for (const d of days) {
            const dt = new Date(d.date);
            if (Number.isNaN(dt.getTime())) continue;

            const local = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
            local.setHours(12, 0, 0, 0);
            if (local < from || local > to) continue;

            const iso = formatISO(local);

            if (!map[iso]) map[iso] = {};
            if (!map[iso][empId]) {
              map[iso][empId] = {
                blocked: false,
                blockTitle: "",
                request: null,
                requestTitle: "",
              };
            }

            const type = d.type;

            if (status === "schváleno" && BLOCKING_TYPES.has(type)) {
              map[iso][empId].blocked = true;
              map[iso][empId].blockTitle = `${humanLeaveType(type)} (schváleno)`;
            }

            const reqCode = requestTypeToShiftCode(type);
            if (reqCode && (status === "čeká" || status === "schváleno")) {
              map[iso][empId].request = reqCode;
              map[iso][empId].requestTitle = `${humanLeaveType(type)} (${status})`;
            }
          }
        }

        setLeaveConstraintsByDate(map);
      } catch (err) {
        console.error("Chyba načítání omezení směn:", err);
        setLeaveConstraintsByDate({});
      } finally {
        setLoadingConstraints(false);
      }
    };

    loadConstraints();
  }, [user, formattedWeekDays, targetPharmacyCode]);

  // =========================
  // Načtení obsazenosti létačů napříč pobočkami pro vybraný týden (review mód)
  // =========================
  useEffect(() => {
    if (!user || !user.firma) return;

    // jen pro admin/rm v review módu
    if (!isReviewMode || !isManager) {
      setFlyingOccupancy({});
      return;
    }

    if (!formattedWeekDays?.length) {
      setFlyingOccupancy({});
      return;
    }

    if (!flyingEmployees?.length) {
      setFlyingOccupancy({});
      return;
    }

    const from = formattedWeekDays[0].date;
    const to = formattedWeekDays[formattedWeekDays.length - 1].date;

    const employeeIds = flyingEmployees.map((e) => e.id).filter(Boolean);
    if (!employeeIds.length) {
      setFlyingOccupancy({});
      return;
    }

    const load = async () => {
      try {
        const qs = new URLSearchParams({
          firma: user.firma,
          employeeIds: employeeIds.join(","),
          from,
          to,
        });

        const res = await fetch(`/api/shifts/occupancy?${qs.toString()}`, {
          credentials: "include",
        });

        const data = await res.json();
        setFlyingOccupancy(data?.occupancy || {});
      } catch (err) {
        console.error("Chyba načítání obsazenosti létačů:", err);
        setFlyingOccupancy({});
      }
    };

    load();
  }, [user, isReviewMode, isManager, formattedWeekDays, flyingEmployees]);


  // =========================
  // Načtení LÉTAČŮ pro vybraný týden (jen review admin/RM)
  // =========================
  useEffect(() => {
    if (!user || !user.firma) return;

    if (!isReviewMode || !isManager) {
      setFlyingEmployees([]);
      return;
    }

    // bez vybraného týdne nemáme co tahat
    if (!weekDays || !Array.isArray(weekDays) || weekDays.length === 0) {
      setFlyingEmployees([]);
      return;
    }

    const weekIso = weekDays[0]?.iso; // pondělí
    if (!weekIso) {
      setFlyingEmployees([]);
      return;
    }

    const loadFlying = async () => {
      try {
        const res = await fetch(
          `/api/employees/${user.firma}/flying/week?pharmacyCode=${encodeURIComponent(
            targetPharmacyCode
          )}&week=${encodeURIComponent(weekIso)}`,
          { credentials: "include" }
        );

        const data = await res.json();

        const items = Array.isArray(data?.items) ? data.items : [];

        // přemapovat do stejného tvaru, jaký používá planner (id, name, role, fullFond)
        const mapped = items.map((e) => ({
          id: String(e._id),
          name: e.fullName,
          role: positionToRole(e.position),
          fullFond: Number(e.denniFond || 0),
          isFlying: true,
          allowedPharmacyCodes: Array.isArray(e.allowedPharmacyCodes) ? e.allowedPharmacyCodes : [],
          scheduleByDate: e.scheduleByDate || {},
          freeOptionsByDate: e.freeOptionsByDate || {},
        }));

        setFlyingEmployees(mapped);
      } catch (err) {
        console.error("Chyba načítání létačů:", err);
        setFlyingEmployees([]);
      }
    };

    loadFlying();
  }, [user, isReviewMode, isManager, targetPharmacyCode, weekDays]);


  // =========================
  // Obsazenost létačů napříč pobočkami (pro tooltip + kolize)
  // =========================
  useEffect(() => {
    if (!user || !user.firma) return;
    if (!isReviewMode || !isManager) {
      setFlyingOccupancy({});
      return;
    }

    if (!formattedWeekDays?.length) {
      setFlyingOccupancy({});
      return;
    }

    const ids = (flyingEmployees || []).map((e) => String(e.id)).filter(Boolean);
    if (ids.length === 0) {
      setFlyingOccupancy({});
      return;
    }

    const fromIso = formattedWeekDays[0].date;
    const toIso = formattedWeekDays[formattedWeekDays.length - 1].date;

    const loadOccupancy = async () => {
      try {
        setLoadingFlyingOccupancy(true);

        const res = await fetch(
          `/api/shifts/occupancy?firma=${encodeURIComponent(user.firma)}&employeeIds=${encodeURIComponent(
            ids.join(",")
          )}&from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
          { credentials: "include" }
        );

        const data = await res.json();
        setFlyingOccupancy(data?.occupancy || {});
      } catch (err) {
        console.error("Chyba načítání obsazenosti létačů:", err);
        setFlyingOccupancy({});
      } finally {
        setLoadingFlyingOccupancy(false);
      }
    };

    loadOccupancy();
  }, [user, isReviewMode, isManager, formattedWeekDays, flyingEmployees]);


  // =========================
  // Načtení ABSENCÍ pro celý měsíc (pro stats panel)
  // =========================
  useEffect(() => {
    if (!user || !user.firma || !targetPharmacyCode) return;

    const loadMonthlyAbsence = async () => {
      try {
        setLoadingMonthlyAbsence(true);

        const res = await fetch(`/api/leaves/${user.firma}/${targetPharmacyCode}`, {
          credentials: "include",
        });

        if (!res.ok) {
          const t = await res.text().catch(() => "");
          console.warn("LEAVES fetch failed:", res.status, t);
          setLeaveConstraintsByDate({});
          return;
        }

        const all = await res.json().catch(() => null);
        if (!Array.isArray(all)) {
          setMonthlyAbsenceByDate({});
          return;
        }

        // měsíční rozsah (lokálně, ať nás nesežere UTC posun)
        const from = new Date(year, month - 1, 1);
        from.setHours(0, 0, 0, 0);

        const to = new Date(year, month - 1, new Date(year, month, 0).getDate());
        to.setHours(23, 59, 59, 999);

        const BLOCKING_TYPES = new Set(["dovolena", "lekar", "nahradni-volno", "jine"]);
        // { [isoDate]: { [employeeId]: { hours, title } } }
        const map = {};

        for (const r of all) {
          if (r?.status !== "schváleno") continue;

          const empId =
            typeof r.employeeId === "string"
              ? r.employeeId
              : r.employeeId?._id || r.employeeId?.id || null;

          if (!empId) continue;

          const days = Array.isArray(r.days) ? r.days : [];
          for (const d of days) {
            const dt = new Date(d.date);
            if (Number.isNaN(dt.getTime())) continue;

            // lokální datum dne (ne ISO UTC)
            const local = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
            local.setHours(12, 0, 0, 0);

            if (local < from || local > to) continue;

            const type = d.type;
            if (!BLOCKING_TYPES.has(type)) continue;

            const iso = formatISO(local);
            const hours = Number(d.hours || 0);

            if (!map[iso]) map[iso] = {};
            if (!map[iso][empId]) {
              map[iso][empId] = { hours: 0, title: "" };
            }

            // ✅ bereme hodiny z žádosti (A), můžeš mít i půldny
            if (Number.isFinite(hours) && hours > 0) {
              map[iso][empId].hours += hours;
              map[iso][empId].title = humanLeaveType(type);
            }
          }
        }

        setMonthlyAbsenceByDate(map);
      } catch (err) {
        console.error("Chyba načítání měsíčních absencí:", err);
        setMonthlyAbsenceByDate({});
      } finally {
        setLoadingMonthlyAbsence(false);
      }
    };

    loadMonthlyAbsence();
  }, [user, targetPharmacyCode, year, month]);


  // =========================
  // Zaměstnanci zobrazení v planneru = kmenoví + (v review) létači
  // =========================
  const displayEmployees = useMemo(() => {
    const base = (!isReviewMode || !isManager)
      ? (employees || [])
      : [...(employees || []), ...(flyingEmployees || [])];

    const roleOrder = { magistr: 1, assistant: 2, sanitar: 3 };
    const isZeroFond = (e) => Number(e.fullFond || 0) <= 0;

    return [...base].sort((a, b) => {
      const aZero = isZeroFond(a);
      const bZero = isZeroFond(b);
      if (aZero !== bZero) return aZero ? 1 : -1;

      const ra = roleOrder[a.role] ?? 99;
      const rb = roleOrder[b.role] ?? 99;
      if (ra !== rb) return ra - rb;

      return a.name.localeCompare(b.name, "cs", { sensitivity: "base" });
    });
  }, [employees, flyingEmployees, isReviewMode, isManager]);

  const timeToMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + (m || 0);
  };

  function isLikelyMallBranch(openingHours) {
    if (!openingHours || typeof openingHours !== "object") return false;

    const sat = openingHours?.["6"];
    const sun = openingHours?.["7"];

    const satOpen = !!(sat && sat.open && sat.close);
    const sunOpen = !!(sun && sun.open && sun.close);

    return satOpen || sunOpen;
  }

  const dayKeyMap = {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    0: "7",
  };

  // =========================
  // COVERAGE (pokrytí dne) pro MonthCalendar
  // =========================
  const getOpeningConfigForDate = (isoDate) => {
    if (!effectiveOpeningHours) return { open: null, close: null, slots: 0 };

    // běžná pobočka = o všech svátcích zavřeno
    // mall / víkendová pobočka = zavřeno jen tehdy, když mají velké obchody zavřeno
    const mallLike = isLikelyMallBranch(effectiveOpeningHours);
    const holidayClosed =
      isCzechHoliday(isoDate) &&
      (!mallLike || isBigShopClosedHoliday(isoDate));

    if (holidayClosed) {
      return { open: null, close: null, slots: 0 };
    }

    const d = makeLocalDate(isoDate);
    const dow = d.getDay();
    const key = dayKeyMap[dow];
    const oh = effectiveOpeningHours?.[key];

    if (!oh || !oh.open || !oh.close) {
      return { open: null, close: null, slots: 0 };
    }

    const openMin = timeToMinutes(oh.open);
    const closeMin = timeToMinutes(oh.close);
    const diff = Math.max(0, closeMin - openMin);

    return {
      open: oh.open,
      close: oh.close,
      slots: Math.floor(diff / 30),
    };
  };

  const computeRoleFilled = (isoDate, roleEmployees, slots) => {
    if (!slots) return [];
    const filled = Array(slots).fill(false);
    const dayShifts = shifts?.[isoDate]?.employees || {};

    // ✅ "?" = nejistá směna (typicky asistenti)
    // Pravidlo pokrytí:
    // - 1× "?" NEvybarví nic (směna není pokryta)
    // - 2× a více "?" vybarví celý den (jako C)
    // (Hodinově se "?" počítá jinde – ve statistikách – jako R/O podle denního fondu.)
    const qCount = (roleEmployees || []).reduce((acc, emp) => {
      const t = dayShifts?.[emp.id]?.type;
      return acc + (t === "?" ? 1 : 0);
    }, 0);

    if (qCount >= 2) return Array(slots).fill(true);


    const getCountForEmp = (emp) => {
      const s = dayShifts?.[emp.id];
      if (!s) return 0;

      if (s.type === "C") return slots;

      // ✅ externisti + kdokoliv s ručně zadanými hodinami
      if ((s.type === "R" || s.type === "O") && typeof s.hours === "number") {
        return Math.min(slots, Math.round(s.hours * 2));
      }

      // fallback: kmenový fond
      return Math.min(slots, (emp.fullFond || 0) * 2);
    };

    // R a C → zleva
    roleEmployees.forEach((emp) => {
      const type = dayShifts?.[emp.id]?.type;
      if (type === "R" || type === "C") {
        const max = getCountForEmp(emp);
        for (let i = 0; i < max; i++) filled[i] = true;
      }
      if (type === "C") {
        for (let i = 0; i < slots; i++) filled[i] = true;
      }
    });

    // O a C → zprava
    roleEmployees.forEach((emp) => {
      const type = dayShifts?.[emp.id]?.type;
      if (type === "O" || type === "C") {
        const max = getCountForEmp(emp);
        for (let i = 0; i < max; i++) filled[slots - 1 - i] = true;
      }
      if (type === "C") {
        for (let i = 0; i < slots; i++) filled[i] = true;
      }
    });

    return filled;
  };

  const isRoleFullyCovered = (isoDate, roleEmployees, slots) => {
    if (!slots) return false;
    if (!roleEmployees?.length) return false;
    const filled = computeRoleFilled(isoDate, roleEmployees, slots);
    return filled.length > 0 && filled.every(Boolean);
  };

  const coverageByDate = useMemo(() => {
    if (!effectiveOpeningHours) return {};
    if (!displayEmployees?.length) return {};

    const mags = displayEmployees.filter((e) => e.role === "magistr");
    const asis = displayEmployees.filter((e) => e.role === "assistant");

    const daysInMonth = new Date(year, month, 0).getDate();
    const map = {};

    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const cfg = getOpeningConfigForDate(iso);

      if (!cfg.slots) continue;

      const magsOk = isRoleFullyCovered(iso, mags, cfg.slots);
      const asisOk = isRoleFullyCovered(iso, asis, cfg.slots);

      if (magsOk && asisOk) map[iso] = "full";
    }

    return map;
  }, [effectiveOpeningHours, displayEmployees, shifts, year, month]);

  const openingConfigs = useMemo(() => {
    if (!effectiveOpeningHours || !formattedWeekDays.length) return {};

    const cfg = {};

    formattedWeekDays.forEach((day) => {
      cfg[day.date] = getOpeningConfigForDate(day.date);
    });

    return cfg;
  }, [effectiveOpeningHours, formattedWeekDays]);

  const updateShift = (date, employeeId, type) => {
    if (readOnly) return;

    setShifts((prev) => {
      const dayShifts = prev[date]?.employees || {};
      const currentObj = dayShifts[employeeId] || null;
      const currentType = currentObj?.type || null;

      // opětovný klik = zrušení (bez promptu)
      const newType = currentType === type ? null : type;

      const newDayEmployees = { ...dayShifts };

      if (!newType) {
        delete newDayEmployees[employeeId];
        return { ...prev, [date]: { employees: newDayEmployees } };
      }

      // zjištění zaměstnance
      const emp = (displayEmployees || []).find((e) => e.id === employeeId);

      const fullFond = Number(emp?.fullFond || 0);

      // externista
      const isExternal = !fullFond || fullFond <= 0;

      // nový režim: malý úvazek
      const isLowFond = fullFond > 0 && fullFond < 6;

      // default pro prompt (když už někdy měl hours)
      const prevHours =
        typeof currentObj?.hours === "number"
          ? currentObj.hours
          : (isLowFond ? Math.max(1, Math.round(fullFond)) : "");

      let hours = undefined;

      // prompt při nastavování R/O
      // - externista
      // - nebo zaměstnanec s denním fondem < 6 h
      if ((isExternal || isLowFond) && (newType === "R" || newType === "O")) {
        const raw = window.prompt(
          `Zadej počet hodin pro ${emp?.name || "externistu"} (${newType}).`,
          prevHours !== "" ? String(prevHours) : ""
        );

        // cancel → nic neměnit
        if (raw === null) return prev;

        const parsed = Number(String(raw).replace(",", ".").trim());

        if (!Number.isFinite(parsed) || parsed <= 0) {
          alert("Neplatná hodnota. Zadej počet hodin (např. 4 nebo 6.5).");
          return prev;
        }

        hours = parsed;
      }

      // uložit
      newDayEmployees[employeeId] = hours ? { type: newType, hours } : { type: newType };

      return { ...prev, [date]: { employees: newDayEmployees } };
    });
  };

  const handleSaveDraft = async () => {
    try {
      const url = planId
        ? `/api/shifts/by-id/${planId}`
        : `/api/shifts/${user.firma}/${targetPharmacyCode}/${year}/${month}`;

      const res = await fetch(url, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shifts }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || data?.message || "Uložení selhalo.");
      }

      setPlanStatus(data.status);
      alert("Návrh uložen.");
    } catch (err) {
      console.error("Chyba při ukládání návrhu:", err);
      alert(err.message || "Chyba při ukládání.");
    }
  };



  const handleSendForApproval = async () => {
    try {
      const res = await fetch(
        `/api/shifts/${user.firma}/${targetPharmacyCode}/${year}/${month}/status`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "pending" }),
        }
      );
      const data = await res.json();
      setPlanStatus(data.status);
      alert("Odesláno ke schválení.");
    } catch (err) {
      alert("Chyba.");
    }
  };

  const handleApprove = async () => {
    try {
      const res = await fetch(
        `/api/shifts/${user.firma}/${targetPharmacyCode}/${year}/${month}/status`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "approved" }),
        }
      );

      const data = await res.json().catch(() => null);

      // ✅ pokud backend vrátí chybu (např. 500), nepokračuj
      if (!res.ok) {
        throw new Error(data?.error || data?.message || "Schválení selhalo.");
      }

      // ✅ stav nastav až po úspěšném schválení
      setPlanStatus(data.status);

      alert("Schváleno.");
    } catch (err) {
      console.error("Chyba při schvalování:", err);
      alert(err.message || "Chyba při schvalování.");
    }
  };


  const getFlyerWeekSlots = (emp) => {
    if (!formattedWeekDays?.length) return [];
    const empId = String(emp?.id || "");

    // helper: z "scheduleByDate" vytáhne type + pharmacyCode (stejná logika jako WeekPlanner)
    const parseElsewhere = (elsewhere) => {
      if (!elsewhere) return null;

      const curPc = targetPharmacyCode != null ? String(targetPharmacyCode) : null;

      const pick = (x) => {
        if (!x || typeof x !== "object") return null;
        const pc = x.pharmacyCode ?? x.pharmacy ?? x.code ?? null;
        const t = x.type ?? x.shift ?? null;
        if (!pc || !t) return null;
        if (curPc && String(pc) === curPc) return null; // stejná pobočka → nebereme jako "jinde"
        return { pharmacyCode: String(pc), type: String(t) };
      };

      if (Array.isArray(elsewhere)) {
        for (const item of elsewhere) {
          const res = pick(item);
          if (res) return res;
        }
        return null;
      }

      if (typeof elsewhere === "object") {
        return pick(elsewhere);
      }

      // string neumíme rozumně parsovat (nevíme pobočku), tak radši nic
      return null;
    };

    return formattedWeekDays.map((d) => {
      const iso = d.date;

      // směna na této pobočce
      const dayShift = shifts?.[iso]?.employees?.[empId] || null;
      if (dayShift) return { iso, kind: "shift", label: dayShift.type || "?" };

      // 1) ✅ primárně bereme "jinde" z scheduleByDate (to ti už funguje v planneru)
      const elsewhere = parseElsewhere(emp?.scheduleByDate?.[iso]);
      if (elsewhere) {
        return {
          iso,
          kind: "otherShift",
          label: elsewhere.type,
          pharmacyCode: elsewhere.pharmacyCode,
        };
      }

      // 2) fallback: obsazenost z occupancy (když je k dispozici)
      const occ = flyingOccupancy?.[empId]?.[iso] || null;
      const occPc = occ?.pharmacyCode ? String(occ.pharmacyCode) : null;
      const occType = occ?.type || null;
      if (occPc && occPc !== String(targetPharmacyCode) && occType) {
        return { iso, kind: "otherShift", label: occType, pharmacyCode: occPc, conflict: !!occ.conflict };
      }

      // blokace (absence)
      const blocked = !!leaveConstraintsByDate?.[iso]?.[empId]?.blocked;
      if (blocked) return { iso, kind: "blocked", label: "×" };

      return { iso, kind: "free", label: "" };
    });
  };


  const ready =
    formattedWeekDays.length > 0 &&
    !loadingEmployees &&
    displayEmployees.length > 0 &&
    !loadingPlan;

  return (
    <div className="px-6 py-4 flex gap-6">
      <div className="flex-1">
      {/* ================= HEADER ================= */}
      <div className="mb-6">
        {/* řádek 1 – název */}
        <h1 className="text-2xl font-semibold text-gray-800 mb-3">
          Plánovač směn
        </h1>

        {/* řádek 2 – tlačítka */}
        <div className="flex gap-3 mb-3">
          {/* ULOŽIT – může vždy, pokud není readOnly */}
          <button
            onClick={handleSaveDraft}
            disabled={readOnly}
            className={`w-36 h-11 rounded-xl text-sm font-medium transition ${
              readOnly
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Uložit
          </button>

          {/* ODESLAT KE SCHVÁLENÍ – pouze pro běžného uživatele (pobočka), a jen když je draft */}
          {!isManager && (
            <button
              onClick={handleSendForApproval}
              disabled={readOnly || planStatus !== "draft"}
              className={`w-36 h-11 rounded-xl text-sm font-medium transition ${
                readOnly || planStatus !== "draft"
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
            >
              <span className="block leading-tight">Odeslat</span>
              <span className="block text-[11px] leading-tight opacity-90">
                ke schválení
              </span>
            </button>
          )}

          {/* SCHVÁLIT – pouze admin/RM, a jen když je draft nebo pending (a není readOnly) */}
          {isManager && (
            <button
              onClick={handleApprove}
              disabled={readOnly || !["draft", "pending"].includes(planStatus)}
              className={`w-36 h-11 rounded-xl text-sm font-medium transition ${
                readOnly || !["draft", "pending"].includes(planStatus)
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              Schválit
            </button>
          )}
        </div>


        {/* řádek 3 – info (3 sloupce) */}
        <div className="grid grid-cols-3 items-center text-sm text-gray-600">
          {/* vlevo */}
          <div className="text-left">
            Plán pro <span className="font-semibold text-gray-800">{month}/{year}</span>
          </div>

          {/* střed */}
          <div className="text-center">
            {isReviewMode && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                REVIEW – pobočka {targetPharmacyCode}
              </span>
            )}
          </div>

          {/* vpravo */}
          <div className="text-right">
            Stav:&nbsp;
            <span className="font-semibold text-gray-800">
              {planStatus === "draft"
                ? "Návrh"
                : planStatus === "pending"
                ? "Čeká na schválení"
                : "Schváleno"}
            </span>
          </div>
        </div>
      </div>
      {/* =============== END HEADER =============== */}

        {!weekDays && !loadingPlan && (
          <div className="text-gray-500 mt-10 text-lg">➤ Vyber týden v kalendáři vpravo.</div>
        )}

        {loadingPlan && (
          <div className="text-gray-500 mt-10 text-lg">Načítám plán směn…</div>
        )}

        {ready && !openingReady && !loadingPlan && (
          <div className="text-gray-500 mt-6 text-sm">Načítám otevírací dobu…</div>
        )}

        {ready && openingReady && (
          <WeekPlanner
            weekDays={formattedWeekDays}
            employees={displayEmployees}
            shifts={shifts}
            updateShift={updateShift}
            openingConfigs={openingConfigs}
            currentMonth={month}
            readOnly={readOnly}
            leaveConstraintsByDate={leaveConstraintsByDate}
            canOverrideConstraints={isManager}
            currentPharmacyCode={targetPharmacyCode}
          />
        )}
      </div>


      {isReviewMode && isManager && (
        <div className="w-[280px] shrink-0 border rounded-xl bg-white shadow-sm p-4 max-h-[520px] overflow-y-auto overflow-x-hidden">

          <div className="font-semibold text-gray-800 mb-2">Létači</div>

          {flyingEmployees.length === 0 ? (
            <div className="text-sm text-gray-500">Zatím nejsou přidáni žádní létači.</div>
          ) : (
            <div className="space-y-2">
              {flyingEmployees.map((emp) => {
                const slots = hoverFlyerId === emp.id ? getFlyerWeekSlots(emp) : null;

                return (
                  <div
                    key={emp.id}
                    className="relative"
                    onMouseEnter={() => setHoverFlyerId(emp.id)}
                    onMouseLeave={() => setHoverFlyerId(null)}
                  >
                    <div className="border rounded-lg p-2 text-sm flex justify-between items-center hover:bg-gray-50 transition">
                      <div>
                        <div className="font-medium">{emp.name}</div>
                        <div className="text-xs text-gray-500">létač</div>
                      </div>
                    </div>

                    {/* jemný tooltip */}
                    {hoverFlyerId === emp.id && (
                      <div className="absolute left-0 top-full mt-2 z-50 w-full rounded-lg border bg-white shadow-lg p-2 overflow-x-hidden">
                        {!formattedWeekDays?.length ? (
                          <div className="text-xs text-gray-500">
                            Vyber týden v kalendáři vpravo – pak uvidíš obsazenost.
                          </div>
                        ) : (
                          <>
                            <div className="text-xs text-gray-600 mb-2">
                              Obsazenost v týdnu
                            </div>

                            {/* popisky dnů (Po–Ne), víkend jemně šedý */}
                            <div className="flex gap-1 mb-1">
                              {formattedWeekDays.map((d) => {
                                const day = d.jsDate.getDay(); // 0=Ne, 6=So
                                const isWknd = day === 0 || day === 6;

                                const short = d.jsDate
                                  .toLocaleDateString("cs-CZ", { weekday: "short" })
                                  .replace(".", "");

                                return (
                                  <div
                                    key={d.date}
                                    className={`w-7 text-[11px] text-center ${isWknd ? "text-gray-400" : "text-gray-600"}`}
                                    title={d.label}
                                  >
                                    {short}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="flex gap-1">
                              {slots.map((s) => {
                                const base =
                                  "w-7 h-7 rounded-md border flex items-center justify-center text-xs select-none";

                                const d = formattedWeekDays.find((x) => x.date === s.iso);
                                const day = d?.jsDate?.getDay?.() ?? -1;
                                const isWknd = day === 0 || day === 6;

                                  const cls =
                                    s.kind === "shift"
                                      ? "bg-blue-600 text-white border-blue-700"
                                      : s.kind === "otherShift"
                                      ? "bg-gray-300 text-gray-800 border-gray-400"
                                      : s.kind === "blocked"
                                      ? "bg-gray-200 text-gray-700 border-gray-300"
                                      : isWknd
                                      ? "bg-gray-50 text-gray-300 border-gray-200"
                                      : "bg-white text-gray-300 border-gray-200";

                                const title =
                                  s.kind === "shift"
                                    ? `Směna (tato pobočka): ${s.label} (${s.iso})`
                                    : s.kind === "otherShift"
                                    ? `Směna jinde: pobočka ${s.pharmacyCode} – ${s.label}${s.conflict ? " (kolize)" : ""} (${s.iso})`
                                    : s.kind === "blocked"
                                    ? `Blokace (absence) (${s.iso})`
                                    : `Volno (${s.iso})`;

                                return (
                                  <div key={s.iso} className={`${base} ${cls}`} title={title}>
                                    {s.label}
                                  </div>
                                );
                              })}
                            </div>


                            <div className="mt-2 text-[11px] text-gray-500">
                              R/O/C = směna (tato pobočka), šedé = směna jinde, × = blokace, prázdné = volno
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}

      <div className="w-[380px]">
        <MonthCalendar
          year={year}
          month={month}
          onWeekSelect={(selectedIso) => {
            const fullWeek = getWeekFromDate(selectedIso);
            setWeekDays(fullWeek);
          }}
          onMonthChange={(y, m) => {
            setYear(y);
            setMonth(m);
          }}
          selectedWeekDates={weekDays ? weekDays.map((d) => d.iso) : []}
          coverageByDate={coverageByDate}
        />

        <div className="mt-4">
          <EmployeeStatsPanel
            employees={displayEmployees}
            shifts={shifts}
            year={year}
            month={month}
            openingHours={effectiveOpeningHours}
            monthlyAbsenceByDate={monthlyAbsenceByDate}
          />

        </div>
        
      </div>
    </div>
  );
}
